import React, { useContext } from 'react';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Button } from '@/shared/components/ui/button';
import { type Connector } from '@/types/swagger';
import { Divider, Tooltip } from '@mui/material';
import CircularProgress from 'components/CircularProgress';
import { CHARGING_PERMISSION } from 'constants/ChargingPermission';
import { useAppSelector } from 'features';
import { getDesyncedChargingConnectorIdsSelector } from 'features/Connectors';
import { getClassByStatus } from 'util/getComponentIcon';
import IntlMessages from 'util/IntlMessages';

import BatteryInfo from './BateryInfo';
import ConnectorTitle from './ConnectorTitle';
import FinalSoc from './FinalSoc';
import NoTransactionWarning from './NoTransactionWarning';
import LongWarningContent from './NoTransactionWarning/LongWarningContent';
import { ConnectorDetailsContainer, ConnectorInfoContainer } from './styled';
import TransactionValues, { type TransactionValuesProps } from './TransactionValues';
import VehicleInfo from './VehicleInfo';

type ConnectorChargingProps = {
  connector: Connector | null;
  isLoading: boolean;
  socLimit: number;
  setSocLimit: (value: number) => void;
  prevSocLimit: number;
  handleUpdateCharge: () => void;
  handleToggleCharge: () => Promise<void>;
} & TransactionValuesProps;

const ConnectorCharging: React.FC<ConnectorChargingProps> = ({
  status,
  vehicle,
  connector,
  batterySoc,
  autonomy,
  donutData,
  absoluteSoc,
  capacity,
  power,
  chargedEnergy,
  startTime,
  endTime,
  transaction,
  estimatedEndTime,
  remainingTime,
  isLoading,
  socLimit,
  setSocLimit,
  prevSocLimit,
  handleUpdateCharge,
  handleToggleCharge,
}) => {
  const { user } = useContext(UserSessionContext);
  const desyncedChargingConnectorIds = useAppSelector(getDesyncedChargingConnectorIdsSelector);
  const connectorHasDesync = connector ? desyncedChargingConnectorIds.includes(connector.id) : true;
  return (
    <ConnectorDetailsContainer className="flex-fill">
      <ConnectorTitle connectorName={connector?.verbose_name || '- - -'} />
      <ConnectorInfoContainer className={getClassByStatus(status as string)}>
        {connectorHasDesync || !vehicle ? (
          <BatteryInfo connector={connector} status={status} />
        ) : (
          <VehicleInfo connector={connector} status={status} />
        )}
      </ConnectorInfoContainer>
      {!connector || connectorHasDesync ? (
        <NoTransactionWarning
          title="Ocurrió un problema al obtener los datos del cargador."
          customContent={<LongWarningContent />}
        />
      ) : (
        <TransactionValues
          connectorId={connector.id}
          batterySoc={batterySoc}
          autonomy={autonomy}
          donutData={donutData}
          absoluteSoc={absoluteSoc}
          capacity={capacity}
          power={power}
          chargedEnergy={chargedEnergy}
          startTime={startTime}
          endTime={endTime}
          status={status}
          transaction={transaction}
          estimatedEndTime={estimatedEndTime}
          remainingTime={remainingTime}
        />
      )}
      <div className="d-flex flex-column flex-fill">
        {!isLoading ? (
          <div className="d-flex flex-column flex-fill justify-content-end">
            <Divider className="my-3" />
            <FinalSoc
              disabled={!user?.groups?.includes(CHARGING_PERMISSION)}
              socLimit={socLimit}
              setSocLimit={setSocLimit}
            />
            <Tooltip
              disableInteractive
              disableHoverListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
              disableTouchListener={!!user?.groups?.includes(CHARGING_PERMISSION)}
              title={<IntlMessages id="components.connector-details.charging-permission" />}
            >
              <div>
                <div className="d-flex flex-column">
                  <Tooltip
                    disableInteractive
                    disableHoverListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                    }
                    disableTouchListener={
                      !user?.groups?.includes(CHARGING_PERMISSION) || socLimit !== prevSocLimit
                    }
                    title={<IntlMessages id="components.connector-details.update-charge-tooltip" />}
                    placement="top"
                  >
                    <div className="mb-3">
                      <Button
                        className="$tw-w-full"
                        variant="secondary"
                        disabled={
                          !user?.groups?.includes(CHARGING_PERMISSION) || socLimit === prevSocLimit
                        }
                        onClick={handleUpdateCharge}
                      >
                        Actualizar SoC máximo
                      </Button>
                    </div>
                  </Tooltip>
                  <Button
                    variant="destructive"
                    disabled={!user?.groups?.includes(CHARGING_PERMISSION)}
                    onClick={handleToggleCharge}
                    className="$tw-w-full"
                  >
                    Detener carga
                  </Button>
                </div>
              </div>
            </Tooltip>
          </div>
        ) : (
          <div className="d-flex flex-column flex-fill justify-content-end">
            <CircularProgress />
          </div>
        )}
      </div>
    </ConnectorDetailsContainer>
  );
};

export default ConnectorCharging;
